import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
// utils
import axios from '../../utils/axios';

import {
  getStorage,
  getMainAdd,
  getFullInfo,
  getFullDisplayInfo,
  getSearch,
  getResourceList,
  getExtraResourceList,
  getFolderList,
  get_recycle_bin_top_level
} from '../../api/Banking';

// ----------------------------------------------------------------------

type BankingState = {
  isLoading: boolean;
  isLoadingFolder: boolean;
  error: boolean;
  StorageList: [];
  addId: [];
  upList: [];
  full_info: [];
  searchList: [];
  breadcrumbsData: [];
  schoolResourceData: { rows: any; total: number };
  resourceSelect: [];
  searchFrom: {};
  // EVI GARTEN APP 预览的参数
  title: string;
  data: {
    res_id: string;
    id: string;
    type: string;
  };
  openModal: boolean;
  recycleBinData: [];
  folderList: [];
  folder: {
    breadcrumbsData: [];
  };
};

const initialState: BankingState = {
  isLoading: false,
  isLoadingFolder: false,
  error: false,
  StorageList: [],
  addId: [],
  upList: [],
  full_info: [],
  searchList: [],
  breadcrumbsData: [],
  schoolResourceData: { rows: [], total: -1 },
  resourceSelect: [],
  searchFrom: {},
  title: '',
  data: {
    res_id: '',
    id: '',
    type: ''
  },
  openModal: false,
  recycleBinData: [],
  // 批量移动文件夹列表
  folderList: [],
  folder: {
    breadcrumbsData: []
  }
};

const slice = createSlice({
  name: 'Banking',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
      state.schoolResourceData = { rows: [], total: -1 };
      state.recycleBinData = [];
    },
    startLoadingFolder(state) {
      state.isLoadingFolder = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.isLoadingFolder = false;
      state.error = action.payload;
    },
    getStorageList(state, action) {
      state.isLoading = false;
      state.StorageList = action.payload;
    },
    getId(state, action) {
      state.isLoading = false;
      state.addId = action.payload;
    },
    getUploadList(state, action) {
      state.isLoading = false;
      state.upList = action.payload;
    },
    getFullInfo(state, action) {
      state.isLoading = false;
      state.full_info = action.payload;
    },
    getSeachDate(state, action) {
      state.isLoading = false;
      state.searchList = action.payload;
    },
    setBreadcrumbsData(state, action) {
      state.breadcrumbsData = action.payload;
    },
    setResourceSelect(state, action) {
      state.resourceSelect = action.payload;
    },

    setSearchFrom(state, action) {
      state.searchFrom = action.payload;
    },
    getSchoolResourceData(state, action) {
      state.isLoading = false;
      state.schoolResourceData = action.payload;
    },
    // append rows for pagination
    appendSchoolResourceData(state, action) {
      state.isLoading = false;
      const payload = action.payload || { rows: [], total: 0 };
      const existingRows = Array.isArray(state.schoolResourceData.rows)
        ? state.schoolResourceData.rows
        : [];
      const newRows = Array.isArray(payload.rows) ? payload.rows : [];
      state.schoolResourceData = {
        rows: [...existingRows, ...newRows],
        total: typeof payload.total === 'number' ? payload.total : state.schoolResourceData.total
      };
    },
    getFolderData(state, action) {
      state.isLoadingFolder = false;
      state.folderList = action.payload;
    },
    setPreviewData(state, { payload }) {
      state.data = payload.data;
      state.openModal = payload.openModal;
      state.title = payload.title;
    },
    clearPreviewData(state) {
      state.data = {
        res_id: '',
        id: '',
        type: ''
      };
      state.openModal = false;
      state.title = '';
    },
    getRecycleBinData(state, action) {
      state.isLoading = false;
      state.recycleBinData = action.payload;
    },
    setFolderBreadcrumbsData(state, action) {
      state.folder.breadcrumbsData = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

export const {
  setBreadcrumbsData,
  setResourceSelect,
  setSearchFrom,
  getSchoolResourceData,
  appendSchoolResourceData,
  setPreviewData,
  clearPreviewData,
  getRecycleBinData,
  getFolderData,
  setFolderBreadcrumbsData
} = slice.actions;

// ----------------------------------------------------------------------

// 獲取佔有空間
export function getStorageData() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getStorage();
      dispatch(slice.actions.getStorageList(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
// 學校資源庫列表數據
export function getSchoolResourceList(params: any) {
  return async () => {
    const offset = params?.offset || 0;
    // For initial load (offset 0) show loading and replace data;
    // for subsequent loads do not clear existing data and append.
    if (!offset) {
      dispatch(slice.actions.startLoading());
    }
    try {
      const response = await getResourceList(params);
      if (offset && offset > 0) {
        dispatch(slice.actions.appendSchoolResourceData(response));
      } else {
        dispatch(slice.actions.getSchoolResourceData(response));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// 額外資源庫列表數據 (Extra Resource)
export function getExtraSchoolResourceList(params: any) {
  return async () => {
    const offset = params?.offset || 0;
    if (!offset) {
      dispatch(slice.actions.startLoading());
    }
    try {
      const response = await getExtraResourceList(params);
      if (offset && offset > 0) {
        dispatch(slice.actions.appendSchoolResourceData(response));
      } else {
        dispatch(slice.actions.getSchoolResourceData(response));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// 新增
export function getAdd(params: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getMainAdd(params);
      dispatch(slice.actions.getId(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUpload(params: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getFullInfo(params);
      dispatch(slice.actions.getUploadList(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getDisplayInfo(params: any) {
  return async (dispatch: any) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getFullDisplayInfo(params);
      dispatch(slice.actions.getFullInfo(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getSearchDate(params: any) {
  return async (dispatch: any) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getSearch(params);
      console.log(response);
      // dispatch(slice.actions.getSeachDate(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRecycleBinList(params: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await get_recycle_bin_top_level(params);
      dispatch(slice.actions.getRecycleBinData(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// 批量移动的文件夹列表
export function fetchFolderList(params: any) {
  return async () => {
    dispatch(slice.actions.startLoadingFolder());
    try {
      const response = await getFolderList(params);
      dispatch(slice.actions.getFolderData(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
